import { useEffect, useState } from 'react';
import type { ListaInvitadosEvento, Plantilla, ResumenInvitados, Rrpp } from '../../../api/ops/listas';
import type { EventoPanel, Invitado } from '../../../api/ops/portalRrpp';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Contexto } from '../../../componentes/Contexto';
import { Desplegable } from '../../../componentes/Desplegable';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { Tabla, type FilaTabla } from '../../../componentes/Tabla';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AvisoVista, Vacia } from '../comun';
import { iniciales, useCarga } from '../uso';
import { Cargando, EstadoDelError, SinPermiso } from '../estados';
import {
    acompanantes,
    apuntadosLista,
    AYUDA,
    colorDe,
    contactoInvitado,
    cuandoEs,
    diaHora,
    ESTADO_EVENTO,
    ESTADO_LISTA,
    estadoInvitado,
    eventoAbierto,
    eventoInicial,
    enPuerta,
    FILTROS_INVITADOS,
    hora,
    lineaLista,
    nombreRrpp,
    opsDe,
    ORIGEN,
    partesAforo,
    PERMISOS,
    personas,
    quienApunta,
    textoLlegaron,
} from '../textos';
import { Apuntar } from './Apuntar';
import { CrearListas } from './CrearListas';
import { FichaInvitado } from './FichaInvitado';
import { FichaLista } from './FichaLista';
import { PanelPlantilla } from './Plantilla';

/**
 * Apartado Listas (10.9): el evento en RV Contexto (el más próximo con listas, o `?evento=`), los avisos de Instagram sin
 * conectar (G517, G556; de `listChannels`) y de la web sin publicar (G675, G730; de `unavailable_reasons` de los enlaces,
 * G788), arriba el aforo de la noche y dos cifras (§21) y las vistas Listas · Invitados · Plantillas. Fichas en RV Panel:
 * lista (Invitados · Ajustes · RRPP), invitado (`?invitado=`), apuntar, plantilla y crear listas.
 */
export type Panel =
    | { tipo: 'lista'; id: string; pestana?: number }
    | { tipo: 'invitado'; id: string }
    | { tipo: 'apuntar'; lista?: string }
    | { tipo: 'plantilla'; plantilla: Plantilla | null }
    | { tipo: 'crear' };

export interface ContextoListas {
    props: PropsPantalla;
    evento: EventoPanel | null;
    listas: readonly ListaInvitadosEvento[];
    rrpp: ReadonlyMap<string, Rrpp>;
    /** Instagram conectado (null: no se sabe; la API responde `409 instagram_not_connected`). */
    instagram: boolean | null;
    /** «Enviar por WhatsApp» encendido en Entregas (G562). */
    whatsapp: boolean;
    abrir: (p: Panel | null) => void;
    recargar: () => void;
}

const VISTAS = [
    { clave: 'listas', etiqueta: 'Listas' },
    { clave: 'invitados', etiqueta: 'Invitados' },
    { clave: 'plantillas', etiqueta: 'Plantillas' },
] as const;

export function ApartadoListas(props: PropsPantalla) {
    const { negocio, movil, soloLectura, busqueda, navegar, ruta } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const ver = permisos.puede(PERMISOS.listasVer);
    const verPlantillas = permisos.puede(PERMISOS.plantillas);
    const apuntar = permisos.puede(PERMISOS.invitadosGestionar) && !soloLectura;
    const vistas = VISTAS.filter((v) => v.clave !== 'plantillas' || verPlantillas);
    const [vista, setVista] = useState<string>(busqueda.has('invitado') ? 'listas' : (busqueda.get('vista') ?? 'listas'));
    const [eventoId, setEventoId] = useState<string | null>(busqueda.get('evento'));
    const [panel, setPanel] = useState<Panel | null>(() => {
        const g = busqueda.get('invitado');
        return g === null ? null : { tipo: 'invitado', id: g };
    });
    const [vuelta, setVuelta] = useState(0);
    const recargar = () => { setVuelta((v) => v + 1); };
    // «Llegaron N → Ver quiénes» (10.9, G981) abre Invitados con el filtro «Ha llegado» (G904).
    const [filtroInvitados, setFiltroInvitados] = useState('');
    const verLlegados = () => {
        setFiltroInvitados('llegado');
        setVista('invitados');
    };

    const eventos = useCarga(ver ? () => ops.listEvents(negocio, { from: new Date(Date.now() - 30 * 86_400_000).toISOString(), limit: 50 }) : null, [negocio, ver, vuelta]);
    const evento = eventos.datos === null ? null : eventoInicial(eventos.datos.data, eventoId);
    const idEvento = evento?.id ?? null;
    const listas = useCarga(idEvento === null ? null : () => ops.listGuestLists(negocio, idEvento), [negocio, idEvento, vuelta]);
    const resumen = useCarga(idEvento === null ? null : () => ops.getEventGuestSummary(negocio, idEvento), [negocio, idEvento, vuelta]);
    const asignaciones = useCarga(idEvento === null ? null : () => ops.listPromoterAssignments(negocio, idEvento), [negocio, idEvento, vuelta]);
    const rrpp = useCarga(permisos.puede(PERMISOS.rrppVer) ? () => ops.listPromoters(negocio, { limit: 100 }) : null, [negocio, vuelta]);
    const canales = useCarga(permisos.puede(PERMISOS.canales) ? () => ops.listChannels(negocio, {}) : null, [negocio]);
    const enlaces = useCarga(idEvento !== null && permisos.puede(PERMISOS.enlacesVer) ? () => ops.listPromoterLinks(negocio, { event_id: idEvento, status: 'active', limit: 50 }) : null, [negocio, idEvento]);
    const envio = useCarga(() => ops.getDeliveryChannelSettings(negocio), [negocio]);

    // Sin Instagram conectado: de `listChannels`; sin poder leerlo, de los motivos de los enlaces (G788).
    const motivos = (enlaces.datos?.data ?? []).flatMap((e) => e.unavailable_reasons ?? []);
    const instagram: boolean | null =
        canales.datos !== null ? canales.datos.data.some((c) => c.channel_type === 'instagram' && (c.status === 'active' || c.status === 'degraded')) : motivos.includes('instagram_not_connected') ? false : null;
    const sinWeb = motivos.includes('site_not_published');
    const mapaRrpp = new Map((rrpp.datos?.data ?? []).map((p) => [p.member_id, p]));
    const ctx: ContextoListas = {
        props,
        evento,
        listas: listas.datos?.data ?? [],
        rrpp: mapaRrpp,
        instagram,
        whatsapp: envio.datos?.whatsapp_enabled === true,
        abrir: (p) => {
            setPanel(p);
            if (p === null && busqueda.has('invitado')) {
                const q = new URLSearchParams(busqueda);
                q.delete('invitado');
                navegar(`${ruta}?${q.toString()}`, true);
            }
        },
        recargar,
    };

    const acciones: AccionCabecera[] = [];
    if (vista === 'plantillas' && verPlantillas && !soloLectura) {
        acciones.push({ clave: 'plantilla', texto: 'Nueva plantilla', icono: 'plus', onClick: () => { setPanel({ tipo: 'plantilla', plantilla: null }); } });
    } else if (apuntar && eventoAbierto(evento)) {
        acciones.push({ clave: 'apuntar', texto: 'Apuntar invitado', icono: 'user-plus', onClick: () => { setPanel({ tipo: 'apuntar' }); } });
    }
    useCabecera({ ayuda: ver ? (AYUDA[vista] ?? AYUDA.listas ?? []) : [], acciones });

    if (!ver) {
        return <SinPermiso props={props} seccion="Listas y RRPP" />;
    }
    if (eventos.error !== null && eventos.datos === null) {
        return <EstadoDelError props={props} error={eventos.error} seccion="Listas y RRPP" nombre="las listas" onReintentar={eventos.recargar} />;
    }
    if (eventos.datos === null) {
        return <Cargando nombre="las listas" />;
    }

    const indice = Math.max(0, vistas.findIndex((v) => v.clave === vista));
    return (
        <div className="rv-ls-apartado">
            <div className="rv-ls-vistas">
                <Filtros etiqueta="Vista" items={vistas.map((v) => ({ etiqueta: v.etiqueta }))} valor={indice} onCambio={(i) => { setFiltroInvitados(''); setVista(vistas[i]?.clave ?? 'listas'); }} />
            </div>
            {vista !== 'plantillas' && (
                <>
                    {eventos.datos.data.length > 0 && evento !== null && (
                        <div className="rv-ls-contexto">
                            <Contexto
                                etiqueta="Evento"
                                icono="ticket"
                                movil={movil}
                                valor={evento.id}
                                opciones={eventos.datos.data.map((e) => ({ valor: e.id, texto: `${e.name ?? ''} · ${cuandoEs(e.starts_at)}` }))}
                                onChange={(v) => { setEventoId(v); }}
                            />
                            {evento.status !== 'published' && <Chip estado={ESTADO_EVENTO[evento.status][1]}>{ESTADO_EVENTO[evento.status][0]}</Chip>}
                        </div>
                    )}
                    {instagram === false && (
                        <AvisoVista
                            icono="instagram"
                            titulo="Instagram no está conectado."
                            texto="Los enlaces de Instagram no salen hasta que conectes la cuenta de Instagram del negocio. Mientras, apunta por email o con el formulario web."
                            boton={permisos.puede(PERMISOS.canalesGestionar) ? { texto: 'Conectar Instagram', icono: 'plug', onClick: () => { navegar('/canales?canal=instagram'); } } : null}
                        />
                    )}
                    {sinWeb && (
                        <AvisoVista
                            icono="globe"
                            titulo="Tu web no está publicada."
                            texto="Los formularios web de tus RRPP no funcionan hasta que la publiques. Mientras, apunta por Instagram o por email."
                            boton={permisos.puede(PERMISOS.webPublicar) ? { texto: 'Ir a la web', icono: 'globe', onClick: () => { navegar('/web'); } } : null}
                        />
                    )}
                </>
            )}
            {vista === 'listas' && <VistaListas ctx={ctx} cargando={listas.datos === null && listas.error === null && evento !== null} error={listas.error} recargar={listas.recargar} resumen={resumen.datos} asignaciones={asignaciones.datos?.data ?? []} sinEventos={eventos.datos.data.length === 0} onVerLlegados={verLlegados} />}
            {vista === 'invitados' && evento !== null && <VistaInvitados key={filtroInvitados} ctx={ctx} filtroInicial={filtroInvitados} />}
            {vista === 'invitados' && evento === null && <Vacia icono="ticket" titulo="Todavía no hay eventos." texto="Crea un evento y sus listas para empezar a apuntar invitados." />}
            {vista === 'plantillas' && <VistaPlantillas ctx={ctx} />}

            {panel?.tipo === 'lista' && <FichaLista ctx={ctx} listaId={panel.id} pestanaInicial={panel.pestana ?? 0} asignaciones={asignaciones.datos?.data ?? []} />}
            {panel?.tipo === 'invitado' && <FichaInvitado ctx={ctx} invitadoId={panel.id} />}
            {panel?.tipo === 'apuntar' && <Apuntar ctx={ctx} listaInicial={panel.lista} asignaciones={asignaciones.datos?.data ?? []} />}
            {panel?.tipo === 'plantilla' && <PanelPlantilla ctx={ctx} plantilla={panel.plantilla} />}
            {panel?.tipo === 'crear' && <CrearListas ctx={ctx} />}
        </div>
    );
}

function VistaListas({ ctx, cargando, error, recargar, resumen, asignaciones, sinEventos, onVerLlegados }: { ctx: ContextoListas; cargando: boolean; error: unknown; recargar: () => void; resumen: ResumenInvitados | null; asignaciones: readonly { lists: { guest_list_id: string; quota?: number | null }[]; status: string }[]; sinEventos: boolean; onVerLlegados: () => void }) {
    const { props, evento, listas } = ctx;
    const { movil, soloLectura } = props;
    const permisos = usePermisos();
    const gestionar = permisos.puede(PERMISOS.listasGestionar) && !soloLectura;
    const apuntar = permisos.puede(PERMISOS.invitadosGestionar) && !soloLectura;
    if (sinEventos || evento === null) {
        return <Vacia icono="ticket" titulo="Todavía no hay eventos." texto="Crea un evento y sus listas para empezar a apuntar invitados." />;
    }
    if (error !== null && listas.length === 0) {
        return <EstadoDelError props={props} error={error} seccion="Listas y RRPP" nombre="las listas" onReintentar={recargar} />;
    }
    if (cargando) {
        return <Cargando nombre="las listas" />;
    }
    const terminado = evento.status === 'ended';
    const abierto = eventoAbierto(evento);
    const vivas = listas.filter((l) => l.status !== 'cancelled');
    const c = evento.capacity_detail;

    const filas: FilaTabla[] = listas.map((l) => {
        const conCupo = asignaciones.filter((a) => a.status === 'active' && a.lists.some((x) => x.guest_list_id === l.id && x.quota !== 0)).length;
        const [quien, quienSub] = quienApunta(l, conCupo);
        const lleno = l.capacity != null && l.guests_count >= l.capacity;
        const quedan = l.capacity == null ? null : Math.max(0, l.capacity - l.guests_count);
        const chip = l.status === 'open' ? null : ESTADO_LISTA[l.status];
        return {
            clave: l.id,
            etiqueta: l.name ?? 'Lista',
            onAbrir: () => { ctx.abrir({ tipo: 'lista', id: l.id }); },
            celdas: {
                lista: (
                    <span className="rv-ls-celda-persona">
                        <span className="rv-ca-baldosa" style={{ background: colorDe(l.color), color: '#fff' }}><Icono nombre="list-checks" /></span>
                        <span className="rv-ca-fila__texto">
                            <strong>{l.name}{chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}</strong>
                            <span>{lineaLista(l, terminado)}</span>
                        </span>
                    </span>
                ),
                apuntados: (
                    <span className="rv-ls-barrita">
                        <strong>{apuntadosLista(l)}</strong>
                        <span className="rv-ls-barrita__fondo"><span style={{ width: `${String(l.capacity ? Math.min(100, Math.round((l.guests_count / l.capacity) * 100)) : 0)}%`, background: lleno ? 'var(--rv-red)' : undefined }} /></span>
                        {quedan !== null && quedan <= 3 && l.status === 'open' && <small>Quedan {quedan}</small>}
                    </span>
                ),
                entrada: (
                    <span className="rv-ca-fila__texto">
                        <strong>{l.door_cutoff_at == null ? 'Sin hora límite' : `Hasta las ${hora(l.door_cutoff_at)}`}</strong>
                        <span>{l.closes_at == null ? 'Se cierra al empezar el evento' : `Se cierra el ${diaHora(l.closes_at)}`}</span>
                    </span>
                ),
                quien: (
                    <span className="rv-ca-fila__texto">
                        <strong>{quien}</strong>
                        <span>{quienSub}</span>
                    </span>
                ),
            },
            tarjeta: (
                <span className="rv-ca-fila__texto">
                    <strong>{l.name}{chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}</strong>
                    <span>{apuntadosLista(l)} · {l.door_cutoff_at == null ? 'Sin hora límite' : `Hasta las ${hora(l.door_cutoff_at)}`} · {quien}</span>
                </span>
            ),
            acciones:
                apuntar && abierto && l.status === 'open' ? (
                    <button type="button" className="rv-ls-fantasma" aria-label={`Apuntar en ${l.name ?? ''}`} onClick={(e) => { e.stopPropagation(); ctx.abrir({ tipo: 'apuntar', lista: l.id }); }}>
                        <Icono nombre="user-plus" />
                    </button>
                ) : undefined,
        };
    });

    const libres = c.available;
    return (
        <>
            {vivas.length > 0 && (
                <div className={`rv-ls-cifras${movil ? ' rv-ls-cifras--movil' : ''}`}>
                    <div className="rv-ls-aforo">
                        <strong className="rv-ls-aforo__titulo">Aforo de la noche</strong>
                        <span className="rv-ls-aforo__cifra">
                            <b>{libres}</b> {terminado ? (libres === 1 ? 'plaza sin usar' : 'plazas sin usar') : libres === 1 ? 'plaza libre' : 'plazas libres'}
                        </span>
                        <small>de {c.capacity} de aforo · {cuandoEs(evento.starts_at)}</small>
                        <span className="rv-ls-apilada" role="img" aria-label={`${String(libres)} plazas libres de ${String(c.capacity)}`}>
                            {partesAforo(evento).map((p) => (
                                <span key={p.etiqueta} style={{ flexGrow: p.valor, background: p.color }} />
                            ))}
                            <span style={{ flexGrow: libres }} className="rv-ls-apilada__libre" />
                        </span>
                        <span className="rv-ls-leyenda">
                            {partesAforo(evento).map((p) => (
                                <span key={p.etiqueta}><i style={{ background: p.color }} />{p.etiqueta} <b>{p.valor} plazas</b></span>
                            ))}
                            <span><i className="rv-ls-apilada__libre" />{terminado ? 'Sin vender' : 'Libres'} <b>{libres} plazas</b></span>
                        </span>
                    </div>
                    <div className="rv-ls-dos-cifras">
                        <div className="rv-ls-cifra">
                            <strong>Apuntados</strong>
                            <span><b>{resumen?.people ?? '—'}</b> {personas(resumen?.people ?? 0)}</span>
                            <small>{vivas.length} {vivas.length === 1 ? 'lista' : 'listas'} · con acompañantes</small>
                        </div>
                        {terminado ? (
                            // G981: con el evento terminado, «Llegaron» en lugar de «Sin escribir». G992: «Llegaron N de M plazas · X %»
                            // compara plazas con plazas (`arrived_people` frente a `people`, las dos de Listas).
                            <div className="rv-ls-cifra">
                                <span className="rv-ls-cifra__cab">
                                    <strong>Llegaron</strong>
                                    <button type="button" className="rv-ls-cifra__ver" onClick={onVerLlegados}>
                                        <span>Ver quiénes</span>
                                        <Icono nombre="chevron-right" />
                                    </button>
                                </span>
                                <span><b>{resumen?.arrived_people ?? '—'}</b> {personas(resumen?.arrived_people ?? 0)}</span>
                                <small>{resumen === null ? '' : textoLlegaron(resumen.arrived_people, resumen.people)}</small>
                            </div>
                        ) : (
                            <div className="rv-ls-cifra">
                                <strong>Sin escribir</strong>
                                <span><b>{resumen?.personal_links_unused ?? '—'}</b> {personas(resumen?.personal_links_unused ?? 0)}</span>
                                <small>Por Instagram: aún no tienen el pase</small>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {filas.length === 0 ? (
                <Vacia
                    icono="list-checks"
                    titulo="Este evento todavía no tiene listas."
                    texto={permisos.puede(PERMISOS.listasGestionar) ? 'Crea sus listas desde tus plantillas en un momento, o una lista suelta solo para este evento.' : 'Las crea el propietario o un administrador.'}
                >
                    {gestionar && abierto && (
                        <Boton icono="plus" onClick={() => { ctx.abrir({ tipo: 'crear' }); }}>
                            Crear las listas
                        </Boton>
                    )}
                </Vacia>
            ) : (
                <>
                    <Tabla
                        etiqueta="Listas del evento"
                        movil={movil}
                        columnas={[
                            { clave: 'lista', titulo: 'Lista', ancho: 'minmax(190px, 1.5fr)' },
                            { clave: 'apuntados', titulo: 'Apuntados', ancho: 'minmax(130px, 0.9fr)' },
                            { clave: 'entrada', titulo: 'Entrada', ancho: 'minmax(150px, 1.1fr)' },
                            { clave: 'quien', titulo: 'Quién apunta', ancho: 'minmax(180px, 1.3fr)' },
                        ]}
                        filas={filas}
                    />
                    {gestionar && abierto && (
                        <div>
                            <Boton tipo="secundario" icono="plus" onClick={() => { ctx.abrir({ tipo: 'crear' }); }}>
                                Añadir listas a este evento
                            </Boton>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function VistaInvitados({ ctx, filtroInicial = '' }: { ctx: ContextoListas; filtroInicial?: string }) {
    const { props, evento, listas, rrpp } = ctx;
    const { movil, negocio, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const [lista, setLista] = useState('');
    const [estado, setEstado] = useState(filtroInicial);
    const [q, setQ] = useState('');
    const [buscar, setBuscar] = useState('');
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    useEffect(() => {
        reiniciar();
    }, [lista, estado, buscar, evento?.id, reiniciar]);
    const filtro = FILTROS_INVITADOS.find((f) => f.valor === estado)?.consulta ?? {};
    const idEvento = evento?.id ?? '';
    const pagina = useCarga(() => ops.listGuests(negocio, { event_id: idEvento, guest_list_id: lista === '' ? undefined : lista, q: buscar.length >= 2 ? buscar : undefined, ...filtro, cursor: pag.cursor ?? undefined, limit: 25 }), [negocio, idEvento, lista, estado, buscar, pag.cursor]);
    const terminado = evento?.status === 'ended';
    const reenviar = permisos.puede(PERMISOS.reenviar) && !soloLectura && !terminado;

    if (pagina.error !== null && pagina.datos === null) {
        return <EstadoDelError props={props} error={pagina.error} seccion="Listas y RRPP" nombre="los invitados" onReintentar={pagina.recargar} />;
    }
    const nombreLista = (id: string) => listas.find((l) => l.id === id)?.name ?? '—';
    const filas: FilaTabla[] = (pagina.datos?.data ?? []).map((g: Invitado) => {
        const e = estadoInvitado(g, terminado);
        const [contacto, contactoSub] = contactoInvitado(g);
        const quien = g.member_id == null ? 'La casa' : nombreRrpp(rrpp.get(g.member_id));
        const sub = `${nombreLista(g.guest_list_id)}${g.companions > 0 ? ` · con ${String(g.companions)} acompañante${g.companions > 1 ? 's' : ''}` : ''}`;
        return {
            clave: g.id,
            etiqueta: g.display_name,
            onAbrir: () => { ctx.abrir({ tipo: 'invitado', id: g.id }); },
            celdas: {
                invitado: (
                    <span className="rv-ls-celda-persona">
                        <span className={`rv-ca-inicial${g.status === 'active' ? ' rv-ca-inicial--roja' : ''}`} aria-hidden="true">{iniciales(g.display_name)}</span>
                        <span className="rv-ca-fila__texto"><strong>{g.display_name}</strong><span>{sub}</span></span>
                    </span>
                ),
                contacto: <span className="rv-ca-fila__texto"><strong>{contacto}</strong>{contactoSub !== '' && <span>{contactoSub}</span>}</span>,
                quien: <span className="rv-ca-fila__texto"><strong>{quien}</strong><span>{ORIGEN[g.origin]}</span></span>,
                estado: (
                    <span className="rv-ca-fila__texto">
                        <strong>{e.chip === null ? e.texto : <Chip estado={e.chip}>{e.texto}</Chip>}</strong>
                        {e.detalle !== '' && <span>{e.detalle}</span>}
                    </span>
                ),
            },
            tarjeta: (
                <span className="rv-ca-fila__texto">
                    <strong>{g.display_name}{e.chip !== null && <Chip estado={e.chip}>{e.texto}</Chip>}</strong>
                    <span>{contacto} · {sub} · {e.chip === null ? `${e.texto} · ` : ''}{quien}</span>
                </span>
            ),
            acciones:
                reenviar && g.status === 'active' && g.pass_state === 'delivered' ? (
                    <button type="button" className="rv-ls-fantasma" aria-label={`Reenviar el pase a ${g.display_name}`} onClick={(ev) => { ev.stopPropagation(); ctx.abrir({ tipo: 'invitado', id: g.id }); }}>
                        <Icono nombre="send" />
                    </button>
                ) : undefined,
        };
    });
    const conFiltros = lista !== '' || estado !== '' || buscar !== '';
    return (
        <>
            <div className={`rv-ls-herramientas${movil ? ' rv-ls-herramientas--movil' : ''}`}>
                <Campo etiqueta="Buscar por nombre" tipo="search" valor={q} onChange={setQ} onEnter={() => { setBuscar(q.trim()); }} nombre="buscar-invitados" />
                <Desplegable etiqueta="Lista" valor={lista} onChange={setLista} opciones={[{ valor: '', texto: 'Todas las listas' }, ...listas.map((l) => ({ valor: l.id, texto: l.name ?? '—' }))]} />
                <Desplegable etiqueta="Estado" valor={estado} onChange={setEstado} opciones={FILTROS_INVITADOS.map((f) => ({ valor: f.valor, texto: f.texto }))} />
            </div>
            {pagina.datos === null ? (
                <Cargando nombre="los invitados" />
            ) : filas.length === 0 ? (
                <Vacia
                    icono="users"
                    titulo={conFiltros ? 'No hay invitados con estos filtros.' : 'Todavía no hay nadie apuntado.'}
                    texto={conFiltros ? 'Prueba con otra lista u otro estado.' : 'Apunta a alguien con su Instagram o su email, o espera a que se apunten por los enlaces de tus RRPP.'}
                >
                    {conFiltros && (
                        <Boton tipo="secundario" icono="x" onClick={() => { setLista(''); setEstado(''); setQ(''); setBuscar(''); }}>
                            Quitar filtros
                        </Boton>
                    )}
                </Vacia>
            ) : (
                <>
                    <Tabla
                        etiqueta="Invitados del evento"
                        movil={movil}
                        columnas={[
                            { clave: 'invitado', titulo: 'Invitado', ancho: 'minmax(190px, 1.4fr)' },
                            { clave: 'contacto', titulo: 'Contacto', ancho: 'minmax(140px, 1fr)' },
                            { clave: 'quien', titulo: 'Quién lo apuntó', ancho: 'minmax(170px, 1.1fr)' },
                            { clave: 'estado', titulo: 'Estado', ancho: 'minmax(150px, 1fr)' },
                        ]}
                        filas={filas}
                    />
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(pagina.datos.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(pagina.datos?.next_cursor); }} movil={movil} />
                </>
            )}
        </>
    );
}

function VistaPlantillas({ ctx }: { ctx: ContextoListas }) {
    const { props } = ctx;
    const { movil, negocio, soloLectura } = props;
    const ops = opsDe(props.ops);
    const plantillas = useCarga(() => ops.listGuestListTemplates(negocio, { limit: 100 }), [negocio, ctx.listas.length]);
    if (plantillas.error !== null && plantillas.datos === null) {
        return <EstadoDelError props={props} error={plantillas.error} seccion="Listas y RRPP" nombre="las plantillas" onReintentar={plantillas.recargar} />;
    }
    if (plantillas.datos === null) {
        return <Cargando nombre="las plantillas" />;
    }
    const orden = [...plantillas.datos.data].sort((x, y) => Number(x.status === 'archived') - Number(y.status === 'archived'));
    if (orden.length === 0) {
        return (
            <Vacia icono="layers" titulo="Todavía no hay plantillas." texto="Crea tus listas tipo (VIP, general, cumpleaños…) y se copiarán a cada evento.">
                {!soloLectura && (
                    <Boton icono="plus" onClick={() => { ctx.abrir({ tipo: 'plantilla', plantilla: null }); }}>
                        Nueva plantilla
                    </Boton>
                )}
            </Vacia>
        );
    }
    const filas: FilaTabla[] = orden.map((t) => {
        const archivada = t.status === 'archived';
        const nPreguntas = t.fields?.length ?? 0;
        return {
            clave: t.id,
            etiqueta: t.name ?? 'Plantilla',
            onAbrir: () => { ctx.abrir({ tipo: 'plantilla', plantilla: t }); },
            celdas: {
                plantilla: (
                    <span className="rv-ls-celda-persona">
                        <span className="rv-ca-baldosa" style={{ background: colorDe(t.color), color: '#fff', opacity: archivada ? 0.6 : 1 }}><Icono nombre="layers" /></span>
                        <span className="rv-ca-fila__texto">
                            <strong>{t.name}{archivada && <Chip estado="neutral">Archivada</Chip>}</strong>
                            <span>{archivada ? 'Archivada: ya no se copia' : t.apply_by_default ? 'Se copia a cada evento nuevo' : 'Solo si la eliges'}</span>
                        </span>
                    </span>
                ),
                aforo: <span className="rv-ca-fila__texto"><strong>{t.capacity == null ? 'Sin límite' : `${String(t.capacity)} personas`}</strong><span>{acompanantes(t.max_companions)}</span></span>,
                entrada: <span className="rv-ca-fila__texto"><strong>{t.door_cutoff_local_time == null ? 'Sin hora límite' : `Hasta las ${t.door_cutoff_local_time}`}</strong><span>{enPuerta(t.door_price, true)}</span></span>,
                quien: <span className="rv-ca-fila__texto"><strong>{t.public_signup === true ? 'Instagram y web' : 'Solo el equipo y sus RRPP'}</strong>{nPreguntas > 0 && <span>{nPreguntas} pregunta{nPreguntas > 1 ? 's' : ''}</span>}</span>,
            },
        };
    });
    return (
        <>
            <Tabla
                etiqueta="Plantillas de lista"
                movil={movil}
                columnas={[
                    { clave: 'plantilla', titulo: 'Plantilla', ancho: 'minmax(200px, 1.5fr)' },
                    { clave: 'aforo', titulo: 'Aforo', ancho: 'minmax(130px, 0.9fr)' },
                    { clave: 'entrada', titulo: 'Entrada', ancho: 'minmax(150px, 0.9fr)' },
                    { clave: 'quien', titulo: 'Quién apunta', ancho: 'minmax(170px, 1.1fr)' },
                ]}
                filas={filas}
            />
            {haySiguiente(plantillas.datos.next_cursor) && <p className="rv-ca-nota rv-ca-nota--gris">Hay más plantillas: se ven las 100 primeras.</p>}
        </>
    );
}
